import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteSweepRoundedIcon from "@mui/icons-material/DeleteSweepRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import { insetSx } from "../center/ownerCenterUi";

const COUNT_LABELS = {
  items: "Ítems / productos",
  erpMovements: "Movimientos ERP",
  stockMovements: "Movimientos de stock",
  stockCountSessions: "Conteos de stock",
  stockCountLines: "Líneas de conteo",
  ledgerEntries: "Asientos contables",
  businessTransactions: "Transacciones",
  cashMovements: "Movimientos de caja",
  cashSessions: "Sesiones de caja",
  fixedExpenses: "Gastos fijos",
  recipes: "Recetas",
  recipeImpacts: "Impactos de recetas",
  productImportDrafts: "Importaciones",
  promotions: "Promociones",
  priceLists: "Listas de precios",
  erpCounters: "Contadores internos",
  erpSettings: "Estado / configuración operativa ERP",
};

function normalizeProjects(projects = []) {
  return (Array.isArray(projects) ? projects : []).filter((row) => row?.id).map((row) => ({
    id: String(row.id),
    name: String(row.name || row.slug || row.id),
    slug: String(row.slug || ""),
  }));
}

export default function OwnerProjectErpResetDialog({ open, onClose, initiativeId, projects = [], onReset }) {
  const options = React.useMemo(() => normalizeProjects(projects), [projects]);
  const [projectId, setProjectId] = React.useState("");
  const [preview, setPreview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [confirmation, setConfirmation] = React.useState("");
  const [success, setSuccess] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    const initial = options[0]?.id || "";
    setProjectId(initial);
    setPreview(null);
    setConfirmation("");
    setError("");
    setSuccess(null);
  }, [open, options]);

  React.useEffect(() => {
    if (!open || !initiativeId || !projectId) return;
    let cancelled = false;
    setLoading(true);
    setPreview(null);
    setError("");
    setConfirmation("");
    ownerApi.projectErpResetPreview(initiativeId, projectId)
      .then((response) => { if (!cancelled) setPreview(response?.preview || null); })
      .catch((err) => { if (!cancelled) setError(formatApiError(err, "No pude revisar el ERP de este proyecto.")); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [initiativeId, open, projectId]);

  const projectName = String(preview?.project?.name || options.find((row) => row.id === projectId)?.name || "");
  const canConfirm = Boolean(preview && !preview.blocked && projectName && confirmation.trim() === projectName.trim() && !submitting);
  const counts = Object.entries(preview?.counts || {}).filter(([, value]) => Number(value || 0) > 0);

  const submit = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    setError("");
    try {
      const response = await ownerApi.resetProjectErp(initiativeId, { projectId, confirmation });
      setSuccess(response?.reset || null);
      setPreview((current) => current ? { ...current, counts: {}, totalRecords: 0 } : current);
      setConfirmation("");
      await onReset?.(response?.reset || null);
    } catch (err) {
      setError(formatApiError(err, "No pude reiniciar el ERP."));
    } finally {
      setSubmitting(false);
    }
  };

  return <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
    <DialogTitle sx={{ pb: .6 }}>
      <Stack direction="row" spacing={.8} alignItems="center">
        <DeleteSweepRoundedIcon color="error" />
        <Box><Typography sx={{ fontWeight: 1000, fontSize: "1.02rem" }}>Reiniciar ERP del cliente</Typography><Typography sx={{ color: "text.secondary", fontSize: ".72rem" }}>Borra la operación ERP y deja el proyecto listo para arrancar de nuevo.</Typography></Box>
      </Stack>
    </DialogTitle>
    <DialogContent dividers sx={{ pt: 1.2 }}>
      <Stack spacing={1.15}>
        {options.length > 1 ? <FormControl fullWidth size="small"><InputLabel>Project WebProfit</InputLabel><Select label="Project WebProfit" value={projectId} onChange={(event) => setProjectId(event.target.value)}>{options.map((row) => <MenuItem key={row.id} value={row.id}>{row.name}{row.slug ? ` · ${row.slug}` : ""}</MenuItem>)}</Select></FormControl> : null}

        <Alert severity="warning" icon={<WarningAmberRoundedIcon />}>
          Esta acción es destructiva. Elimina ítems y registros operativos ERP. <b>No toca</b> clientes, pedidos, usuarios, sitio, dominio, Billing ni configuración/comprobantes fiscales.
        </Alert>

        {loading ? <Typography sx={{ color: "text.secondary", fontSize: ".8rem" }}>Revisando registros del ERP…</Typography> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}
        {success ? <Alert severity="success">ERP reiniciado. Se eliminaron <b>{Number(success.totalDeleted || 0).toLocaleString("es-AR")}</b> registros operativos.</Alert> : null}

        {preview ? <>
          <Box sx={{ ...insetSx, p: 1.1 }}>
            <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
              <Box sx={{ minWidth: 0 }}><Typography sx={{ fontWeight: 1000, fontSize: ".83rem" }}>{preview.project?.name}</Typography><Typography sx={{ color: "text.secondary", fontSize: ".67rem" }}>Se eliminarían {Number(preview.totalRecords || 0).toLocaleString("es-AR")} registros</Typography></Box>
              <Chip size="small" color={preview.blocked ? "error" : "warning"} label={preview.blocked ? "Bloqueado" : "Listo para reiniciar"} sx={{ fontWeight: 900 }} />
            </Stack>
            {counts.length ? <Box sx={{ mt: .85, display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: .55 }}>{counts.map(([key, value]) => <Box key={key} sx={{ p: .7, border: "1px solid", borderColor: "divider", borderRadius: 1.2 }}><Typography sx={{ color: "text.secondary", fontSize: ".63rem" }}>{COUNT_LABELS[key] || key}</Typography><Typography sx={{ fontWeight: 1000, fontSize: ".9rem" }}>{Number(value || 0).toLocaleString("es-AR")}</Typography></Box>)}</Box> : <Typography sx={{ mt: .7, color: "text.secondary", fontSize: ".72rem" }}>No hay registros ERP para eliminar.</Typography>}
          </Box>

          {preview.blocked ? <Alert severity="error">Hay {Number(preview.fiscalDocuments || 0).toLocaleString("es-AR")} comprobante(s) fiscal(es). El reinicio masivo se bloquea para no romper trazabilidad legal.</Alert> : null}

          {!preview.blocked && Number(preview.totalRecords || 0) > 0 && !success ? <>
            <Divider />
            <Box><Typography sx={{ fontWeight: 950, fontSize: ".76rem" }}>Confirmación final</Typography><Typography sx={{ mt: .2, color: "text.secondary", fontSize: ".68rem" }}>Escribí exactamente <b>{projectName}</b>.</Typography></Box>
            <TextField size="small" fullWidth value={confirmation} onChange={(event) => setConfirmation(event.target.value)} placeholder={projectName} autoComplete="off" />
          </> : null}
        </> : null}
      </Stack>
    </DialogContent>
    <DialogActions sx={{ px: 2, py: 1.2 }}>
      <Button onClick={onClose} disabled={submitting} sx={{ textTransform: "none", fontWeight: 900 }}>{success ? "Cerrar" : "Cancelar"}</Button>
      {!success && preview && Number(preview.totalRecords || 0) > 0 ? <Button color="error" variant="contained" startIcon={<DeleteSweepRoundedIcon />} disabled={!canConfirm} onClick={submit} sx={{ textTransform: "none", fontWeight: 1000 }}>{submitting ? "Reiniciando…" : "Borrar ERP y empezar de cero"}</Button> : null}
    </DialogActions>
  </Dialog>;
}
